import type { QueryClient } from '@tanstack/react-query';
import {
  Users,
  Box,
  Boxes,
  DollarSign,
  Ticket,
  Gift,
  Receipt,
  Package,
  Camera,
  Layers,
  Database,
  ArrowRightLeft,
  Languages,
} from 'lucide-react';
import { Link, redirect, type RouteObject } from 'react-router-dom';

import { getGamesQueryOptions } from '@/api/getGames';
import { getSandboxesQueryOptions } from '@/api/getSandboxes';
import { getTranslationLanguagesQuery } from '@/api/translation/getTranslationLanguages';
import { SandboxBreadcrumbDropdown } from '@/features/sandbox/components/sandboxBreadcrumbDropdown';
import { ensureAppQueryData } from '@/hooks/useAppQuery';
import { updateApiDefaults } from '@/hooks/useGameSandboxSwitch';
import { SANDBOX_LOCAL_STORAGE, storeGameSandbox } from '@/lib/utils';
import type { Game } from '@/models/game';
import type { PlayerModel } from '@/models/players';

import Dashboard from './dashboard';
import { GameLayout } from './GameLayout';
import { giftRoute } from './gift/route';
import { itemRoute } from './items/route';
import { managementRoute } from './management/route';
import { seasonPassRoute } from './seasonPass/route';
import { storeRoute } from './store/route';
import { transactionsRoute } from './transactions/route';
import { translationsRoute } from './translations/route';

const MainDashboard = (
  <Dashboard
    menu
    items={[
      {
        link: 'players',
        label: 'Players',
        icon: <Users size={20} />,
        items: [],
      },
      {
        link: 'items',
        label: 'Catalog',
        icon: <Database size={20} />,
        items: [
          {
            link: 'item',
            label: 'Items',
            icon: <Box size={16} />,
          },
          {
            link: 'bundle',
            label: 'Bundles',
            icon: <Boxes size={16} />,
          },
          {
            link: 'currency',
            label: 'Currency',
            icon: <DollarSign size={16} />,
          },
        ],
      },
      {
        link: 'season-passes',
        label: 'Season Pass',
        icon: <Ticket size={20} />,
        items: [],
      },
      {
        link: 'gifts',
        label: 'Gifts',
        icon: <Gift size={20} />,
        items: [],
      },
      {
        link: 'translations',
        label: 'Translations',
        icon: <Languages size={20} />,
        items: [],
      },
      {
        link: 'transactions',
        label: 'Transactions',
        icon: <ArrowRightLeft size={20} />,
        items: [
          {
            link: 'store',
            label: 'Store',
            icon: <Receipt size={20} />,
          },
          {
            link: 'inventory',
            label: 'Inventory',
            icon: <Package size={20} />,
          },
        ],
      },
      {
        link: 'store/snapshot',
        label: 'Snapshot',
        icon: <Camera size={16} />,
      },
    ]}
  />
);

// Game-level dashboard with only sandboxes
const GameDashboard = (
  <Dashboard
    menu
    items={[
      {
        link: 'sandboxes',
        label: 'Sandboxes',
        icon: <Layers size={20} />,
        items: [],
      },
    ]}
  />
);

export const gameRoute = (queryClient: QueryClient): RouteObject => ({
  path: 'game/:gameCode',
  id: 'game',
  handle: {
    crumb: (game: Game) => (
      <Link to={`/game/${game.game_code}/sandboxes`}>{game.name}</Link>
    ),
  },
  loader: ({ params }) => {
    return queryClient
      .ensureQueryData(getGamesQueryOptions())
      .then((data) => data.games.find((g) => g.game_code === params.gameCode));
  },
  children: [
    {
      // Index route: redirect to saved sandbox if exists and valid, otherwise to sandboxes page
      index: true,
      loader: async ({ params }) => {
        const { gameCode } = params;
        if (gameCode) {
          const savedSandboxes = localStorage.getItem(SANDBOX_LOCAL_STORAGE);
          if (savedSandboxes) {
            const parsed = JSON.parse(savedSandboxes) as {
              game: string;
              sandbox: string;
            }[];
            const found = parsed.find((s) => s.game === gameCode);
            if (found) {
              // Validate that the saved sandbox still exists
              const { sandboxes } = await queryClient.ensureQueryData(
                getSandboxesQueryOptions({ game_code: gameCode }),
              );
              const sandboxExists = sandboxes.some(
                (s) => s.code === found.sandbox,
              );
              if (sandboxExists) {
                return redirect(found.sandbox);
              }
            }
          }
        }
        return redirect('sandboxes');
      },
    },
    // Game-level routes (no sandbox required)
    {
      element: GameDashboard,
      children: [
        {
          path: 'sandboxes',
          lazy: () => import('./sandboxes'),
        },
      ],
    },
    // Sandbox-level routes
    {
      path: ':sandboxCode',
      id: 'sandbox', // Used by SandboxBreadcrumbDropdown to get the sandbox match data
      handle: {
        crumb: ({ sandboxCode }: { sandboxCode: string }) => (
          <SandboxBreadcrumbDropdown sandboxCode={sandboxCode} />
        ),
      },
      loader: ({ params }) => {
        const { gameCode, sandboxCode } = params;
        if (!gameCode || !sandboxCode) {
          throw new Error('Game code and sandbox code are required');
        }
        // Set Axios defaults using shared utility - overwrites previous values on each navigation
        updateApiDefaults(gameCode, sandboxCode);

        // Persist to localStorage for next visit
        storeGameSandbox(gameCode, sandboxCode);

        // Warm the sandbox-scoped language list so it's available everywhere
        // (e.g. the item modal) without each consumer refetching. Non-blocking:
        // navigation shouldn't wait on it.
        void ensureAppQueryData(
          queryClient,
          { gameCode, sandboxCode },
          getTranslationLanguagesQuery(),
        );

        return { sandboxCode };
      },
      element: <GameLayout />,
      children: [
        { index: true, loader: () => redirect('players') },
        {
          element: MainDashboard,
          children: [
            {
              path: 'players',
              children: [
                {
                  index: true,
                  lazy: () => import('./players'),
                },
                {
                  path: ':playerId',
                  id: 'player',
                  handle: {
                    crumb: ({ player }: { player: PlayerModel }) =>
                      player.platform_user_id,
                  },
                  lazy: async () => {
                    const { loader, Player } = await import('./player');
                    return {
                      loader: loader(queryClient),
                      Component: Player,
                    };
                  },
                },
              ],
            },
            { ...itemRoute },
            { ...seasonPassRoute },
            { ...giftRoute },
            { ...translationsRoute(queryClient) },
            { ...transactionsRoute },
            { ...storeRoute(queryClient) },
            { ...managementRoute },
          ],
        },
      ],
    },
  ],
});
